import { createContext, FC, ReactNode, useContext, useEffect, useState } from 'react';

import { useFeedInvalidation } from '../api/hooks/feedSocket';
import { FeedSocket, FeedSocketClient } from '../socket';
import WebTokenManager from '../utils/WebTokenManager';

/**
 * feed.events 릴레이 소켓을 앱 단위로 1회 연결하고 하위에 제공한다.
 * - 자체적으로 React Query invalidate 를 구동(useFeedInvalidation).
 * - PatientProvider 등 하위가 useFeedSocketClient() 로 같은 소켓을 구독해 스토어(로스터)를 갱신할 수 있다(단일 연결 공유).
 * 인증 이후(MainLayout) 마운트. 릴레이는 단일 소켓.
 */
const FeedSocketContext = createContext<FeedSocketClient | undefined>(undefined);

export const useFeedSocketClient = () => useContext(FeedSocketContext);

const FeedSocketProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [socket, setSocket] = useState<FeedSocketClient>();

  useEffect(() => {
    if (!WebTokenManager.hasToken()) return;
    const token = WebTokenManager.getToken();
    if (!token) return;
    const client = FeedSocket.connect(token, ['patient', 'survey']);
    // 외부 시스템(소켓)에 마운트 시 1회 연결 후 그 인스턴스를 하위(context)·useFeedInvalidation에
    // 리액티브하게 전파해야 하므로 state 저장이 필수인 정당한 구독 패턴. 런타임상 유효하여
    // React Compiler는 이 컴포넌트를 정상 최적화한다.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSocket(client);
    return () => client.deactivate();
  }, []);

  useFeedInvalidation(socket);

  return <FeedSocketContext.Provider value={socket}>{children}</FeedSocketContext.Provider>;
};

export default FeedSocketProvider;
